import React from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Paper,
  Typography,
  } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { colors, inputStates } from '../theme';
import { StandardInput } from './StandardInput';
import {
  useGooglePlaces,
  PlacePrediction,
  ParsedAddress,
} from '../hooks/useGooglePlaces';

import { PixelDots } from './ConstructionLoader';
interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (address: ParsedAddress) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  fullWidth?: boolean;
  error?: boolean;
}

// Replicate StandardInput's editable sx styles so the Autocomplete input matches exactly
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 36,
    backgroundColor: inputStates.editable.background,
    '& fieldset': {
      borderColor: inputStates.editable.border || 'transparent',
      borderWidth: inputStates.editable.border ? '1.5px' : 0,
    },
    '&:hover fieldset': {
      borderColor: inputStates.editable.border || 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.blue,
      borderWidth: '2px',
    },
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'left' as const,
    color: inputStates.editable.text,
    fontWeight: 400,
    fontSize: '0.875rem',
  },
};

function GooglePaper(props: React.ComponentProps<typeof Paper>) {
  return (
    <Paper
      {...props}
      sx={{
        borderRadius: '8px',
        border: `1px solid ${colors.grey[300]}`,
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        mt: 0.5,
        ...((props.sx as object) || {}),
      }}
    >
      {props.children}
      <Box
        sx={{
          borderTop: `1px solid ${colors.grey[300]}`,
          px: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <img
          src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png"
          alt="Powered by Google"
          style={{ height: 16 }}
        />
      </Box>
    </Paper>
  );
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onAddressSelect,
  label = 'Address',
  placeholder = 'Street address',
  required = false,
  fullWidth = false,
  error = false,
}) => {
  const {
    isLoaded,
    loadError,
    predictions,
    isLoadingPredictions,
    fetchPredictions,
    getPlaceDetails,
    clearPredictions,
  } = useGooglePlaces();

  const handleInputChange = (
    _event: React.SyntheticEvent,
    newValue: string,
    reason: string
  ) => {
    // 'input' = user typing, 'reset' = option selected (MUI sets input to getOptionLabel),
    // 'clear' = clear button clicked
    if (reason === 'input') {
      onChange(newValue);
      fetchPredictions(newValue);
    } else if (reason === 'clear') {
      onChange('');
      clearPredictions();
    }
    // 'reset' is handled by handleOptionSelect — don't update onChange here
    // to avoid overwriting with the full description string
  };

  const handleOptionSelect = async (
    _event: React.SyntheticEvent,
    selected: string | PlacePrediction | null
  ) => {
    if (!selected || typeof selected === 'string') return;

    clearPredictions();

    const parsed = await getPlaceDetails(selected.placeId);
    if (parsed) {
      // onAddressSelect sets all 4 fields (street, city, state, zip) in one setState call.
      // Do NOT call onChange separately — it would overwrite with stale state.
      onAddressSelect(parsed);
    } else {
      // Fallback: at least set the street from the suggestion text
      onChange(selected.mainText);
    }
  };

  // Fallback: if the API is unavailable, render a plain StandardInput
  if (loadError || !isLoaded) {
    return (
      <StandardInput
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth={fullWidth}
        required={required}
        error={error}
      />
    );
  }

  return (
    <Autocomplete
      freeSolo
      options={predictions}
      loading={isLoadingPredictions}
      inputValue={value}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      fullWidth={fullWidth}
      PaperComponent={GooglePaper}
      noOptionsText="No addresses found"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          variant="outlined"
          size="small"
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          sx={textFieldSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoadingPredictions && (
                  <span style={{ display: 'inline-flex', marginRight: 8, color: colors.grey[400] }}><PixelDots label="Loading suggestions" /></span>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...restProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
        return (
          <li key={key} {...restProps}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
              <LocationOnIcon
                sx={{ color: colors.grey[400], fontSize: 20, flexShrink: 0 }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: colors.grey[900],
                  }}
                >
                  {option.mainText}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    color: colors.grey[600],
                  }}
                >
                  {option.secondaryText}
                </Typography>
              </Box>
            </Box>
          </li>
        );
      }}
      slotProps={{
        listbox: {
          sx: {
            maxHeight: 300,
            '& .MuiAutocomplete-option': {
              px: 2,
              py: 1,
              '&:hover': {
                backgroundColor: colors.blueTint,
              },
              '&[aria-selected="true"]': {
                backgroundColor: colors.blueTint,
              },
            },
          },
        },
      }}
    />
  );
};
